/**
 * 65.28, suppression's cue for the player (lane E's look; decision 161, the owner: "I think yes, but not sure how far we'll
 * go with the suppressed status and player impact" — so a cue only: no aim sway, nothing taken from the controls). Two
 * layers over the view, both dark, both at the edge:
 *   - **the edge**: the screen's rim darkens while the mark lasts, as deep as the mark is fresh (`SUPPRESSION_CUE.edge` at
 *     a full mark, easing out as it runs down) — the state;
 *   - **the pulse**: each round that cracks past (the mark renewed) closes the vignette in for a moment and lets it go
 *     (`pulse` s) — the event, so a burst reads as a burst.
 * `suppressionCue` is pure: the HUD feeds it the seconds the mark has left (PlayerState.suppressed) and the seconds since
 * it last rose; `SUPPRESSION_CUE_CSS` is the two layers' look, for the HUD's two divs (opacity from the cue).
 */

export const SUPPRESSION_CUE = {
  /** the mark's length (constants SUPPRESSION.seconds): the edge is full at this many seconds left */
  seconds: 2,
  /** the edge's opacity at a full mark; it eases out by (left / seconds) ^ fade */
  edge: 0.85, fade: 0.6,
  /** a near round's pulse: how long, and how strong at its start (it falls off as a square) */
  pulse: 0.24, pulseStrength: 0.9,
} as const;

/** The two layers' opacities for a mark with `left` seconds left, `sinceRound` seconds after it last rose (< 0: none yet). Pure. */
export function suppressionCue(left: number, sinceRound: number): { edge: number; pulse: number } {
  const C = SUPPRESSION_CUE;
  const edge = left > 0 ? C.edge * Math.min(1, left / C.seconds) ** C.fade : 0;
  const pulse = sinceRound >= 0 && sinceRound < C.pulse ? C.pulseStrength * (1 - sinceRound / C.pulse) ** 2 : 0;
  return { edge, pulse };
}

/** The two layers (absolute, over the whole view, no pointer events): the dark rim, and a tighter vignette for the pulse. */
export const SUPPRESSION_CUE_CSS = {
  edge: 'position:absolute; inset:0; pointer-events:none; box-shadow: inset 0 0 260px 60px rgba(0,0,0,0.6); opacity:0;',
  pulse: 'position:absolute; inset:0; pointer-events:none; background: radial-gradient(ellipse at center, rgba(0,0,0,0) 48%, rgba(0,0,0,0.5) 78%, rgba(0,0,0,0.8) 100%); opacity:0;',
} as const;

/**
 * Follows a unit's mark over frames and gives the cue: the seconds since it last rose come from watching `left` rise (each
 * near round renews it). Presentation state; one per view.
 */
export class SuppressionCueTracker {
  private was = 0;
  private roseAt = -Infinity;
  /** `left`: the mark's seconds left now (0 / undefined: none); `now`: the view's clock in seconds. */
  update(left: number | undefined, now: number): { edge: number; pulse: number } {
    const l = left ?? 0;
    if (l > this.was + 1e-6) this.roseAt = now;
    this.was = l;
    return suppressionCue(l, Number.isFinite(this.roseAt) ? now - this.roseAt : -1);
  }
}
